import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X, Loader2 } from "lucide-react";

/**
 * Searchable product selector (server-side, debounced) for picking a single
 * product from the catalog. When a product is selected it shows a compact
 * confirmed chip with a clear button.
 * Props: value (code), valueName, onSelect(product), onClear, testId, placeholder
 */
export default function ProductPicker({ value, valueName, onSelect, onClear, testId = "product-picker", placeholder = "Ürün kodu veya adı ara..." }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const c = q.trim();
    if (!c) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: c, limit: 8 } });
        setResults(data);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (value) {
    return (
      <div data-testid={`${testId}-selected`} className="flex items-center justify-between gap-2 rounded-md border border-teal-300 bg-teal-50 px-3 py-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-teal-800 truncate">{value}</div>
          {valueName && <div className="text-xs text-teal-700 truncate">{valueName}</div>}
        </div>
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onClick={onClear}
          className="shrink-0 h-7 w-7 rounded-full bg-white border border-teal-200 text-teal-700 flex items-center justify-center hover:bg-teal-100"
          aria-label="Seçimi kaldır"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid={`${testId}-input`}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="h-11 pl-9 text-sm"
        />
        {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <div data-testid={`${testId}-results`} className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {results.map((p) => (
            <button
              key={p.id || p.code}
              type="button"
              data-testid={`${testId}-option-${p.code}`}
              onClick={() => { onSelect(p); setQ(""); setResults([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
            >
              <div className="text-sm font-medium text-slate-900">{p.code}</div>
              <div className="text-xs text-slate-500 truncate">{p.name}</div>
            </button>
          ))}
        </div>
      )}
      {open && q.trim() && !loading && results.length === 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-md border border-slate-200 bg-white shadow-lg px-3 py-2 text-xs text-slate-500">
          Eşleşen ürün yok
        </div>
      )}
    </div>
  );
}
